import React, { useEffect, useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import {
  Button,
  Card,
  CardContent,
  Typography,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from "@mhome/ui";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import { useRecoilValue } from "recoil";
import { wsReadyState } from "../global-state";
import TelegramIcon from "@mui/icons-material/Telegram";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import SettingsIcon from "@mui/icons-material/Settings";
import PersonIcon from "@mui/icons-material/Person";
import {
  listConnections,
  getTelegramConnectionToken,
  disconnectTelegram,
  updateConnectionSettings,
  checkTelegramTokenStatus,
} from "../service/messaging-service";
import ConnectionSettingsDialog from "./connection-settings-dialog";
import { useToast } from "../common/toast";
import { useComponentBackgroundColor } from "../common/adaptive-theme-provider";
import { useWebSocket } from "../ws/websocket";

const TelegramPage = () => {
  const getComponentBgColor = useComponentBackgroundColor();
  const { successToast, errorToast } = useToast();
  const history = useHistory();
  const { sendAndWaitWs } = useWebSocket();
  const [connections, setConnections] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState({
    connecting: false,
    error: null,
  });
  const [loading, setLoading] = useState(true);
  const [pollingActive, setPollingActive] = useState(false);
  const [telegramUrl, setTelegramUrl] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [settingsDialog, setSettingsDialog] = useState({
    open: false,
    connection: null,
  });
  const wsReady = useRecoilValue(wsReadyState);
  // Refs to store interval IDs for cleanup
  const pollIntervalRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const timeoutRef = useRef(null);

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setPollingActive(false);
    setConnectionStatus((prev) => ({
      ...prev,
      connecting: false,
    }));
    setTelegramUrl(null);
    setCountdown(0);
  };

  useEffect(() => {
    if (wsReady) {
      checkConnectionStatus();
    }
  }, [wsReady]);

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  const checkConnectionStatus = async () => {
    setLoading(true);
    try {
      const response = await listConnections(sendAndWaitWs, "telegram");

      if (response && response.connections) {
        setConnections(response.connections);
        setConnectionStatus({
          connecting: false,
          error: null,
        });
      } else {
        setConnections([]);
        setConnectionStatus((prev) => ({
          ...prev,
          connecting: false,
          error: null,
        }));
      }
    } catch (error) {
      console.error("Error checking Telegram connection:", error);
      setConnections([]);
      setConnectionStatus((prev) => ({
        ...prev,
        connecting: false,
        error: "Error checking connection status",
      }));
    }
    setLoading(false);
  };

  const startConnection = async () => {
    try {
      setConnectionStatus((prev) => ({
        ...prev,
        connecting: true,
        error: null,
      }));

      const response = await getTelegramConnectionToken(sendAndWaitWs);

      if (response && response.code && response.botUsername) {
        const url = `https://t.me/${response.botUsername}?start=${response.code}`;
        setTelegramUrl(url);
        window.open(url, "_blank");
        startPolling(response.code);
      } else {
        throw new Error("Failed to get connection token");
      }
    } catch (error) {
      console.error("Error starting Telegram connection:", error);
      setConnectionStatus((prev) => ({
        ...prev,
        connecting: false,
      }));
      errorToast("Failed to start connection process");
    }
  };

  const startPolling = (token) => {
    // Clear any existing polling first
    stopPolling();

    setPollingActive(true);
    setCountdown(180); // Start countdown at 180 seconds

    pollIntervalRef.current = setInterval(async () => {
      try {
        const response = await checkTelegramTokenStatus(sendAndWaitWs, token);

        if (response && response.success) {
          // Connection successful - refresh connections list
          await checkConnectionStatus();
          stopPolling();
          successToast("Successfully connected to Telegram!");
        }
      } catch (error) {
        console.error("Error during polling:", error);
      }
    }, 3000);

    // Countdown timer
    countdownIntervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          // Time expired
          stopPolling();
          return 0;
        }
        return prev - 1;
      });
    }, 1000); // Update countdown every second

    // Timeout after 3 minutes (180 seconds)
    timeoutRef.current = setTimeout(() => {
      stopPolling();
    }, 180000); // 3 minute timeout to match countdown
  };

  const handleDisconnect = async (messengerId) => {
    try {
      const response = await disconnectTelegram(sendAndWaitWs, messengerId);

      if (response && !response.error) {
        // Refresh connections list
        await checkConnectionStatus();
        successToast("Successfully disconnected from Telegram");
      } else {
        errorToast("Failed to disconnect from Telegram");
      }
    } catch (error) {
      console.error("Error disconnecting from Telegram:", error);
      errorToast("Error disconnecting from Telegram");
    }
  };

  const handleBack = () => {
    history.push("/messaging");
  };

  const handleConnectionClick = (connection) => {
    setSettingsDialog({
      open: true,
      connection: connection,
    });
  };

  const handleSettingsDialogClose = () => {
    setSettingsDialog({
      open: false,
      connection: null,
    });
  };

  const handleSettingsSave = async (updatedConnection) => {
    try {
      const response = await updateConnectionSettings(
        sendAndWaitWs,
        updatedConnection.messengerId,
        {
          active: updatedConnection.active,
          notificationSetting: updatedConnection.notificationSetting,
          preferredName: updatedConnection.preferredName,
        }
      );

      if (response && !response.error) {
        // Refresh connections list
        await checkConnectionStatus();
        successToast("Settings updated successfully");
        handleSettingsDialogClose();
      } else {
        errorToast("Failed to update settings");
      }
    } catch (error) {
      console.error("Error updating settings:", error);
      errorToast("Error updating settings");
    }
  };

  const reopenTelegramLink = () => {
    if (telegramUrl) {
      window.open(telegramUrl, "_blank");
    }
  };

  if (loading) {
    return (
      <div
        style={{
          padding: "16px",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  return (
    <div style={{ padding: "16px" }}>
      <Card
        style={{
          marginBottom: "8px",
          backgroundColor: getComponentBgColor("hsl(var(--card))"),
        }}
      >
        <CardContent style={{ paddingTop: "16px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <Typography variant="h6" style={{ flexGrow: 1 }}>
              Telegram Connections
            </Typography>
            <Button
              variant="contained"
              startIcon={<TelegramIcon />}
              onClick={startConnection}
              style={{
                backgroundColor: "#0088cc",
                boxShadow: "none",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#006699";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#0088cc";
              }}
            >
              Add Connection
            </Button>
          </div>

          {connections.length === 0 && !connectionStatus.connecting && (
            <div>
              <Typography variant="body2" className="text-muted-foreground">
                Connect your Telegram account to receive notifications and
                messages from your smart home system.
              </Typography>
              <Button
                variant="contained"
                startIcon={<TelegramIcon />}
                onClick={startConnection}
                style={{
                  backgroundColor: "#0088cc",
                  marginTop: "16px",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#006699";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#0088cc";
                }}
              >
                Connect to Telegram
              </Button>
            </div>
          )}

          {connectionStatus.connecting && (
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  marginBottom: "16px",
                }}
              >
                <CircularProgress size={24} />
                <div>
                  <Typography variant="body2">
                    Waiting for you to complete the connection in Telegram...
                  </Typography>
                  <Typography
                    variant="body2"
                    className="text-muted-foreground"
                    style={{
                      marginTop: "4px",
                    }}
                  >
                    This link will expire in {countdown} seconds
                  </Typography>
                </div>
              </div>
              <Button
                variant="outlined"
                startIcon={<TelegramIcon />}
                onClick={reopenTelegramLink}
                style={{
                  borderColor: "#0088cc",
                  color: "#0088cc",
                  boxShadow: "none",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#006699";
                  e.currentTarget.style.backgroundColor =
                    "rgba(0, 136, 204, 0.04)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#0088cc";
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                Open Link Again
              </Button>
            </div>
          )}

          {connections.length > 0 && (
            <div>
              <Typography
                variant="body2"
                className="text-muted-foreground"
                style={{
                  marginBottom: "16px",
                }}
              >
                Click on a connection to configure its settings.
              </Typography>

              <List style={{ padding: 0 }}>
                {connections.map((connection, index) => (
                  <ListItem
                    key={`${connection.messengerId}-${index}`}
                    onClick={() => handleConnectionClick(connection)}
                    className="border border-border rounded-2xl cursor-pointer hover:border-primary transition-colors"
                    style={{
                      marginBottom: "8px",
                      padding: "12px",
                      paddingRight: "48px",
                      position: "relative",
                    }}
                  >
                    <ListItemText
                      primary={
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <Typography
                            variant="body1"
                            style={{ fontWeight: 500 }}
                          >
                            {connection.preferredName || "Unnamed"}
                          </Typography>
                          <Chip
                            label={connection.active ? "Active" : "Inactive"}
                            size="small"
                            color={connection.active ? "success" : "default"}
                            variant={connection.active ? "filled" : "outlined"}
                            style={{ height: 20, fontSize: "0.75rem" }}
                          />
                        </div>
                      }
                      secondary={
                        <Typography
                          variant="caption"
                          className="text-muted-foreground"
                        >
                          ID: {connection.messengerId}
                        </Typography>
                      }
                      style={{ margin: 0 }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        right: "16px",
                        top: "50%",
                        transform: "translateY(-50%)",
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleConnectionClick(connection);
                        }}
                      >
                        <SettingsIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </ListItem>
                ))}
              </List>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Connection Settings Dialog */}
      <ConnectionSettingsDialog
        open={settingsDialog.open}
        onClose={handleSettingsDialogClose}
        connection={settingsDialog.connection}
        onSave={handleSettingsSave}
      />
    </div>
  );
};

export default TelegramPage;
